import { useEffect, useRef, useState } from "react"
import { filterSimFiles, SIM_ACCEPT } from "../../oio-sim/files"
import { uploadReason } from "../../nira2/sim/SimStart"
import { uploadRefusalMessage } from "@/lib/quotaGate"
import type { SimEngine } from "../engine"
import { usePasteFiles } from "../usePasteFiles"
import { trackRefused, trackUploadFailed, trackUploaded, uploadFailReason, type AskAnswer } from "../simAnalytics"
import { alreadyInMessage, unavailableMessage, uploadedMessage } from "./messages"
import "./documentCard.css"

interface Props {
  engine: SimEngine
  threadId: string
  payload: { description: string; why: string; needed_for_stage: string }
  send: (message: string) => Promise<boolean>
  disabled?: boolean
  /** Called once after an answer lands (analytics). */
  onAnswered?: (how: AskAnswer) => void
}

/**
 * The agent is missing a document: upload it (one file or several), say it is not available, point to one already in,
 * or answer in text. The upload posts one "Uploaded ..." naming every file once all of them are read. `useDocs` lists
 * documents that are not failed, so an uploaded document that leaves the list is one that could not be read.
 */
export function DocumentRequestCard({ engine, threadId, payload, send, disabled, onAnswered }: Props) {
  const docs = engine.useDocs(threadId)
  const [docIds, setDocIds] = useState<string[]>([])
  const [uploading, setUploading] = useState("")
  const [error, setError] = useState("")
  const [sending, setSending] = useState(false)
  const [asText, setAsText] = useState(false)
  const [text, setText] = useState("")
  const file = useRef<HTMLInputElement>(null)
  const card = useRef<HTMLDivElement>(null)
  const posted = useRef(false)
  const names = useRef<Record<string, string>>({})
  const seen = useRef(new Set<string>())
  const [retry, setRetry] = useState(false)
  // A sent answer keeps the card locked (no double submit); only a failed send frees it.
  const [done, setDone] = useState(false)
  // Focus once, when the ask appears; later re-renders leave focus alone.
  useEffect(() => { card.current?.focus() }, [])

  const mine = docIds.map((id) => ({ id, doc: docs.find((d) => d.docId === id) }))
  for (const m of mine) if (m.doc) seen.current.add(m.id)
  // Only a document that was in the list and then left it could not be read; before it first shows up it is just not listed yet.
  const unreadable = uploading ? [] : mine.filter((m) => !m.doc && seen.current.has(m.id))
  const readyMine = mine.flatMap((m) => (m.doc?.status === "ready" ? [m.doc] : []))
  const reading = mine.filter((m) => m.doc && m.doc.status !== "ready").map((m) => m.doc!.filename)
  const settled = !uploading && mine.length > 0 && readyMine.length + unreadable.length === mine.length
  const ready = docs.filter((d) => d.status === "ready")

  const post = async (message: string, how: AskAnswer = "upload"): Promise<boolean> => {
    setSending(true)
    try {
      const ok = await send(message)
      if (!ok) setError("Couldn't reach the simulator. Try again.")
      else { setDone(true); onAnswered?.(how) }
      return ok
    } finally {
      setSending(false)
    }
  }

  const uploadedText = readyMine.length ? uploadedMessage(readyMine.map((d) => d.filename).join(", "), payload.description) : ""
  const unreadableNote = unreadable.length
    ? `Couldn't read ${unreadable.map((m) => names.current[m.id] || "a file").join(", ")}. Upload ${unreadable.length > 1 ? "them" : "it"} again or answer in text.`
    : ""
  useEffect(() => {
    if (!settled || posted.current) return
    if (!uploadedText) { setError(unreadableNote); return }
    posted.current = true
    setError(unreadableNote)
    void post(uploadedText).then((ok) => { if (!ok) { posted.current = false; setRetry(true) } })
    // `post` only closes over setters and send.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settled, uploadedText, unreadableNote])

  const upload = async (picked: File[], via: "picker" | "paste") => {
    if (!picked.length) return
    const check = filterSimFiles(picked)
    if (!check.files.length) { trackRefused(engine.kind, check.message); setError(check.message); return }
    setError(check.message)
    if (check.message) trackRefused(engine.kind, check.message)
    posted.current = false
    seen.current = new Set()
    names.current = {}
    setRetry(false)
    setDocIds([])
    setUploading(check.files.map((f) => f.name).join(", "))
    const ids: string[] = []
    const failed: string[] = []
    const refused: string[] = []
    try {
      for (const f of check.files) {
        try {
          const row = (await engine.uploadDoc(threadId, f)) as { doc_id?: string } | undefined
          if (row?.doc_id) { ids.push(row.doc_id); names.current[row.doc_id] = f.name }
        } catch (err) {
          trackUploadFailed(engine.kind, uploadFailReason(err))
          const own = uploadRefusalMessage(err)
          if (own) refused.push(own)
          else failed.push(`${f.name}: ${uploadReason(err)}`)
        }
      }
      if (ids.length) trackUploaded(engine.kind, check.files.filter((f) => Object.values(names.current).includes(f.name)), via)
      const lines = [...(failed.length ? [`Couldn't upload ${failed.join("; ")}.`] : []), ...refused]
      if (lines.length) setError(lines.join(" "))
      setDocIds(ids)
    } finally {
      setUploading("")
    }
  }

  const sendUploaded = () => {
    if (!uploadedText) return
    posted.current = true
    setError("")
    setRetry(false)
    void post(uploadedText).then((ok) => { if (!ok) { posted.current = false; setRetry(true) } })
  }
  const answer = (message: string, how: AskAnswer) => { posted.current = true; return post(message, how) }
  const busy = !!disabled || sending || done
  const waiting = !!uploading || reading.length > 0
  usePasteFiles(card, (files) => void upload(files, "paste"), !busy && !waiting)

  return (
    <div ref={card} className="ndc" role="group" aria-label="Document request" tabIndex={-1}>
      <p className="ndc-ov">Document needed · {payload.needed_for_stage}</p>
      <p className="ndc-ask">{payload.description}</p>
      {payload.why ? <p className="ndc-why">{payload.why}</p> : null}
      <div className="ndc-acts">
        <button type="button" className="ncf-btn primary" disabled={busy || waiting} onClick={() => file.current?.click()}>Upload files</button>
        <span className="ndc-note">one or several, or paste an image</span>
        <button type="button" className="ncf-btn" disabled={busy} onClick={() => void answer(unavailableMessage(payload.description), "not_available")}>Not available</button>
        <button type="button" className="ncf-btn" disabled={busy} onClick={() => setAsText((v) => !v)}>{asText ? "Hide text answer" : "Answer in text"}</button>
        <input ref={file} type="file" hidden multiple accept={SIM_ACCEPT} data-testid="request-file" onChange={(e) => { const picked = Array.from(e.target.files ?? []); e.target.value = ""; void upload(picked, "picker") }} />
      </div>
      {ready.length ? (
        <div className="ndc-have" role="group" aria-label="Documents already in">
          <span className="ndc-have-h">Or pick one that is already in</span>
          {ready.map((d) => (
            <button key={d.docId} type="button" className="ncf-btn" disabled={busy} onClick={() => void answer(alreadyInMessage(d.filename, payload.description), "already_in")}>
              It's already in: {d.filename}
            </button>
          ))}
        </div>
      ) : null}
      {asText ? (
        <form className="ndc-text" onSubmit={(e) => { e.preventDefault(); if (text.trim()) void answer(text.trim(), "text") }}>
          <input value={text} aria-label="Your answer" placeholder="Give the detail in your own words" autoFocus onChange={(e) => setText(e.target.value)} />
          <button type="submit" className="ncf-btn primary" disabled={busy || !text.trim()}>Send</button>
        </form>
      ) : null}
      {uploading ? <p className="ndc-note" role="status">Uploading {uploading}…</p> : null}
      {reading.length ? <p className="ndc-note" role="status">Reading {reading.join(", ")}…</p> : null}
      {error ? (
        <p className="ndc-note" role="alert">
          {error}
          {retry ? <> <button type="button" className="ncf-btn" disabled={busy} onClick={sendUploaded}>Retry</button></> : null}
        </p>
      ) : null}
    </div>
  )
}
